import {
  DecoratedControlCell,
  DeviceWithInstalledPower,
  Direction,
  ProductInput,
  moveControlEnergyFocusDown,
  moveControlEnergyFocusLeft,
  moveControlEnergyFocusRight,
  moveControlEnergyFocusUp,
} from '@e2m-customer-portal/domains';
import { ForecastSlice, MarketLocation, useInputFieldLimits, useLocale } from '@e2m-customer-portal/hooks';
import { InputFieldType, getInputFieldTypes, isBattery } from '@e2m-customer-portal/shared';
import clsx from 'clsx';
import {
  Interval,
  addHours,
  eachHourOfInterval,
  endOfDay,
  isBefore,
  isWeekend,
  startOfDay,
  startOfWeek,
  subHours,
} from 'date-fns';
import React, { Fragment, ReactElement, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

interface ControlDeviceTableBodyProps {
  marketLocation: MarketLocation;
  device: DeviceWithInstalledPower;
  deadlineDate?: Date;
  selectedProducts: Set<InputFieldType>;
  slices: readonly ForecastSlice[];
  interval: Interval;
  daysInForecast: Date[];
  readOnly?: boolean;
  resetTrigger?: boolean;
  overviewMap: Map<string, ProductInput[][][]>;
  editedSliceProducts: Map<string, Set<InputFieldType>>;
  isHighlighted: boolean;
  setOverviewMap: React.Dispatch<React.SetStateAction<Map<string, ProductInput[][][]>>>;
  setEditedSliceProducts: React.Dispatch<React.SetStateAction<Map<string, Set<InputFieldType>>>>;
  setErroneousSliceProducts: React.Dispatch<React.SetStateAction<Map<string, Set<InputFieldType>>>>;
  setIsHighlighted: React.Dispatch<React.SetStateAction<boolean>>;
  setRevokeDowntimeInterval: React.Dispatch<React.SetStateAction<Interval<Date> | null>>;
  saveChanges: () => void;
}

export function ControlDeviceTableBody({
  marketLocation,
  device,
  deadlineDate,
  selectedProducts,
  slices,
  interval,
  daysInForecast,
  readOnly,
  resetTrigger,
  overviewMap,
  editedSliceProducts,
  isHighlighted,
  setOverviewMap,
  setEditedSliceProducts,
  setErroneousSliceProducts,
  setIsHighlighted,
  setRevokeDowntimeInterval,
  saveChanges,
}: Readonly<ControlDeviceTableBodyProps>): ReactElement<ControlDeviceTableBodyProps> {
  const [t] = useTranslation();
  const { code: locale } = useLocale();
  const limits = useInputFieldLimits(marketLocation.id, device.id, interval);
  const deviceProducts = isBattery(marketLocation.technologies)
    ? getInputFieldTypes(device.products, 'bands').filter(
        (field) => ![InputFieldType.AFRR_NEGATIVE, InputFieldType.AFRR_POSITIVE].includes(field),
      )
    : getInputFieldTypes(device.products, 'bands');
  const switchFocus = useCallback(
    (sourceRow: number, sourceColumn: number, sourceProductIndex: number, direction?: Direction) => {
      const deviceMap = overviewMap.get(device.id);
      if (!deviceMap) return false;

      const { selectionStart, selectionEnd, value } = deviceMap[sourceRow][sourceColumn][sourceProductIndex].input!;
      const lastRow = deviceMap.length - 1;
      const lastColumn = daysInForecast.length - 1;
      const lastProductIndex = deviceProducts.length - 1;
      let row = sourceRow;
      let column = sourceColumn;
      let productIndex = sourceProductIndex;

      switch (direction) {
        case Direction.Up:
          ({ row, column, productIndex } = moveControlEnergyFocusUp(
            sourceRow,
            sourceColumn,
            sourceProductIndex,
            lastRow,
            lastColumn,
            lastProductIndex,
            deviceMap,
          ));
          break;
        case Direction.Down:
          ({ row, column, productIndex } = moveControlEnergyFocusDown(
            sourceRow,
            sourceColumn,
            sourceProductIndex,
            lastRow,
            lastColumn,
            lastProductIndex,
            deviceMap,
          ));
          break;
        case Direction.Left:
          if (selectionStart !== 0) return false;
          ({ row, column, productIndex } = moveControlEnergyFocusLeft(
            sourceRow,
            sourceColumn,
            sourceProductIndex,
            lastRow,
            lastColumn,
            lastProductIndex,
            deviceMap,
          ));
          break;
        case Direction.Right:
          if (selectionEnd !== value.length) return false;
          ({ row, column, productIndex } = moveControlEnergyFocusRight(
            sourceRow,
            sourceColumn,
            sourceProductIndex,
            lastRow,
            lastColumn,
            lastProductIndex,
            deviceMap,
          ));
          break;
      }

      deviceMap[row][column][productIndex].input?.focus();
      return true;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [daysInForecast.length, device.id, deviceProducts.length],
  );

  return (
    <tbody data-testid="device-table-body">
      {eachHourOfInterval(
        {
          start: startOfDay(startOfWeek(daysInForecast[0], { weekStartsOn: 1 })),
          end: endOfDay(startOfWeek(daysInForecast[0], { weekStartsOn: 1 })),
        },
        { step: 4 },
      ).map((date, rowIndex) => (
        <tr key={`row-${device.id}-${date.toISOString()}`} className="group/last-row">
          <th
            className={clsx(
              'min-w-24 border-0 border-b border-solid pl-6 text-start text-sm font-normal leading-4',
              deviceProducts.length >= 2
                ? 'border-blue-grey-100'
                : 'border-grey-100 group-last-of-type/last-row:border-blue-grey-100',
            )}
          >
            {`${date.toLocaleTimeString(locale, {
              hour: '2-digit',
              minute: '2-digit',
            })} - ${addHours(date, 4).toLocaleTimeString(locale, {
              hour: '2-digit',
              minute: '2-digit',
              hourCycle: 'h24',
            })}`}
          </th>
          <th className="p-0">
            <div className="flex flex-col">
              {deviceProducts.map((product, productIndex) => {
                return (
                  <div
                    key={product}
                    className={clsx(
                      'border-0 border-b border-solid',
                      deviceProducts.length >= 2 && productIndex === deviceProducts.length - 1
                        ? 'border-blue-grey-100'
                        : 'border-grey-100',
                      {
                        'bg-grey-50': (productIndex + (deviceProducts.length % 2 ? rowIndex : 0)) % 2 === 0,
                        'group-last-of-type/last-row:border-blue-grey-100':
                          deviceProducts.length < 2 && productIndex === deviceProducts.length - 1,
                      },
                    )}
                  >
                    <div
                      className={clsx(
                        'min-w-[calc(4rem-2px)] border-0 border-l border-solid border-grey-100 px-1 text-start text-sm font-normal leading-6',
                        {
                          'text-black text-opacity-60': selectedProducts && !selectedProducts.has(product),
                        },
                      )}
                    >
                      {t(`SHARED_PRODUCT_${product}`)}
                    </div>
                  </div>
                );
              })}
            </div>
          </th>
          {slices
            .filter(
              (_, index) => index >= rowIndex * daysInForecast.length && index < (rowIndex + 1) * daysInForecast.length,
            )
            .map((slice, columnIndex) => (
              <Fragment key={`slice-${slice.id}`}>
                <DecoratedControlCell
                  device={device}
                  deadlineDate={deadlineDate}
                  products={deviceProducts}
                  selectedProducts={selectedProducts}
                  slice={slice}
                  limits={limits}
                  cellIndex={{ row: rowIndex, column: columnIndex }}
                  readOnly={
                    (marketLocation.marketingEnd != null && !isBefore(slice.date, marketLocation.marketingEnd)) ||
                    readOnly
                  }
                  resetTrigger={resetTrigger}
                  isHighlighted={isHighlighted}
                  overviewMap={overviewMap}
                  editedSliceProducts={editedSliceProducts}
                  setIsHighlighted={setIsHighlighted}
                  setRevokeDowntimeInterval={setRevokeDowntimeInterval}
                  setOverviewMap={setOverviewMap}
                  setEditedSliceProducts={setEditedSliceProducts}
                  setErroneousSliceProducts={setErroneousSliceProducts}
                  switchFocus={switchFocus}
                  saveChanges={saveChanges}
                />
                {columnIndex === daysInForecast.length - 1 && (
                  <td className="relative p-0">
                    <div className="absolute inset-0 flex flex-col">
                      {deviceProducts.map((product, productIndex) => (
                        <div
                          key={product}
                          className={clsx(
                            'group/cell relative box-border flex-auto border-0 border-b border-solid',
                            deviceProducts.length >= 2 && productIndex === deviceProducts.length - 1
                              ? 'border-blue-grey-100'
                              : 'border-grey-100',
                            {
                              'bg-grey-50': (productIndex + (deviceProducts.length % 2 ? rowIndex : 0)) % 2 === 0,
                              'group-last-of-type/last-row:border-blue-grey-100':
                                deviceProducts.length < 2 && productIndex === deviceProducts.length - 1,
                            },
                          )}
                        >
                          <div
                            className={clsx('absolute inset-0', {
                              'bg-blue-grey-200/5': isWeekend(
                                subHours(slice.date, slice.date.getTimezoneOffset() / 60),
                              ),
                            })}
                          />
                        </div>
                      ))}
                    </div>
                  </td>
                )}
              </Fragment>
            ))}
        </tr>
      ))}
    </tbody>
  );
}
